/*
 * The player's account on the game server: sign-up, sign-in, and the token every request to
 * the server carries. The server lives on the same origin as the page (in development Vite
 * forwards /api and /ws to it, see vite.config.ts). The token is kept in localStorage so the
 * player stays signed in on this device until it expires or they sign out.
 */

import { TERMS_VERSION } from './legal';
import { apiUrl } from './server';

const SESSION_KEY = 'am27.session';

export interface Account {
  username: string;
  token: string;
  /** The Android game's full version comes with this account (the Play review account). */
  full?: boolean;
}

/** Why a request to the server failed: the server's error code, or `offline`. */
export class ServerError extends Error {
  constructor(
    readonly code: string,
    readonly status = 0,
  ) {
    super(code);
  }
}

let account: Account | null = readSession();
const listeners = new Set<(a: Account | null) => void>();

function readSession(): Account | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<Account>) : null;
    return parsed?.username && parsed.token
      ? { username: parsed.username, token: parsed.token, full: parsed.full === true }
      : null;
  } catch {
    return null;
  }
}

function setAccount(next: Account | null): void {
  account = next;
  try {
    if (next) localStorage.setItem(SESSION_KEY, JSON.stringify(next));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    // Without storage the player signs in again next time.
  }
  for (const listener of listeners) listener(next);
}

export function currentAccount(): Account | null {
  return account;
}

/** Called whenever the player signs in or out (or the session turns out to have expired). */
export function onAccountChange(listener: (a: Account | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** A JSON request to the server API; signs the player out on 401 for authenticated calls. */
export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), {
      method,
      headers: {
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(account ? { authorization: `Bearer ${account.token}` } : {}),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ServerError('offline');
  }
  let data: unknown = null;
  try {
    data = await res.json();
  } catch {
    // Not JSON (no server behind /api): treated as unreachable below.
  }
  if (!res.ok || data === null) {
    const code = (data as { error?: string } | null)?.error ?? (res.ok ? 'offline' : 'server');
    if (res.status === 401 && account && path !== '/login') setAccount(null);
    throw new ServerError(data === null && !res.ok ? 'offline' : code, res.status);
  }
  return data as T;
}

/** Signs in (by user name or e-mail address) or signs up (name, address and password). */
export async function signIn(
  kind: 'login' | 'register',
  fields: { login: string; email?: string; password: string },
): Promise<Account> {
  const result = await api<Account>(
    'POST',
    `/${kind}`,
    kind === 'login'
      ? { login: fields.login, password: fields.password }
      : {
          username: fields.login,
          email: fields.email ?? '',
          password: fields.password,
          // The terms of use agreed to on the form (recorded with the account).
          terms: TERMS_VERSION,
        },
  );
  setAccount({ username: result.username, token: result.token, full: result.full === true });
  return result;
}

export function signOut(): void {
  setAccount(null);
}

/** Deletes the signed-in account for good (its saves and profile too), then signs out. */
export async function deleteAccount(password: string): Promise<void> {
  await api('DELETE', '/me', { password });
  setAccount(null);
}

/**
 * Checks the stored session with the server (it may have expired, or the account's full version
 * changed); keeps it when offline.
 */
export async function checkSession(): Promise<void> {
  if (!account) return;
  try {
    const me = await api<{ username: string; full?: boolean }>('GET', '/me');
    const full = me.full === true;
    if (account && full !== (account.full === true)) setAccount({ ...account, full });
  } catch {
    // 401 already signed the player out; other failures leave the session for a retry.
  }
}
